// Adapt both persisted journeys to the existing classroom progress view. This
// is display state only; the API owns journey choice and student eligibility.
export const hasStartedSameGroupings = (point) => Boolean(
  point?.sameGroupsProgress?.started || point?.groupingDecision === "same_groups"
);

const getStartedDifferentGroupingsPoints = (task) => {
  if (!task || task.archivedAt) return [];
  if (task.progressStatus === "not-started" && task.scopeStatus !== "known") return [];
  const selected = new Set(task.selectedProgressionPointIds || []);
  return (task.progressionPoints || []).filter((point) => selected.has(point.id));
};

export const hasClassroomJourneyProgress = (entry, task) => Boolean(
  entry?.progressionPoints?.some((point) => hasStartedSameGroupings(point) || point.journeyConflict) ||
  getStartedDifferentGroupingsPoints(task).length
);

const sameGroupingsProgressPoint = (point) => {
  const same = point.sameGroupsProgress;
  const complete = same?.completedCount || 0;
  const expected = same?.totalCount;
  return {
    id: point.key,
    progressionKey: point.key,
    journey: "same_groups",
    // Older responses can resume Yes without supplying trustworthy totals.
    classroomProgress: { expected, complete, outstanding: point.countRequiringSetup },
    confirmedClassrooms: same?.completedClassrooms || [],
    setupAssignments: same?.setupAssignments || [],
    progress: same && complete >= expected ? "released" : "information_received",
  };
};

const conflictedProgressPoint = (point, different) => {
  const completed = [...(point.sameGroupsProgress?.completedClassrooms || []), ...(different?.confirmedClassrooms || [])];
  return {
    ...different,
    id: point.key,
    progressionKey: point.key,
    journey: "conflict",
    progress: "information_received",
    confirmedClassrooms: [...new Map(completed.map((classroom) => [classroom.id, classroom])).values()],
  };
};

export const getClassroomJourneyProgress = (entry, task) => {
  const points = new Map(getStartedDifferentGroupingsPoints(task).map((point) => [
    point.progressionKey || point.id,
    { ...point, journey: "different_groupings" },
  ]));
  for (const point of entry?.progressionPoints || []) {
    const different = points.get(point.key);
    if (point.journeyConflict || (different && hasStartedSameGroupings(point))) {
      points.set(point.key, conflictedProgressPoint(point, different));
    } else if (hasStartedSameGroupings(point)) {
      points.set(point.key, sameGroupingsProgressPoint(point));
    }
  }
  if (!points.size) return null;
  const progressionPoints = [...points.values()];
  return { progressionPoints, selectedProgressionPointIds: progressionPoints.map((point) => point.id) };
};

export const hasCompletedClassroomJourneys = (entry, task) => {
  if (!entry || (task && task.academicYear !== entry.academicYear)) return false;
  const progress = getClassroomJourneyProgress(entry, task);
  if (!progress) return false;
  const completedKeys = new Set();
  for (const point of progress.progressionPoints) {
    if (point.progress !== "released" || point.unassignedChildren?.length ||
      point.setupAssignments?.length || Number(point.classroomProgress?.outstanding) > 0) return false;
    completedKeys.add(point.progressionKey || point.id);
  }
  // Untouched groups and repair work must not disappear when another group finishes.
  return (entry.progressionPoints || []).every((point) =>
    point.countRequiringSetup === 0 || (point.enabled !== false && completedKeys.has(point.key))
  );
};
